import { privateRequest, type ApiResponse } from "../axios";

export interface DepartmentResponse<T> {
  TotalRecords: number;
  Items: T[];
}

export interface Department {
  Id: number;
  CompanyId: number;
  DepartmentCode: string;
  Name: string;
  ParentId: number;
  ParentName: string;
  DepartmentHeadId: number | null;
  DepartmentHead: string | null;
  Description: string;
  IsActive: boolean | string;
  CreatedDate: string;
  ModifiedDate: string;
  CreatedBy: string;
  ModifiedBy: string;
}

export interface DepartmentPayload {
  DepartmentCode: string;
  DepartmentHeadId: number;
  Description: string;
  IsActive: string; // consider boolean if API allows
  Name: string;
  ParentId: number;
}

export interface DepartmentHeadResponse {
  TotalRecords: number;
  Members: UserAttendance[];
}

export interface UserAttendance {
  RowNumber: number;
  UserId: number;
  UserName: string;
  Email: string;
  ManagerId: number;
  ManagerName: string;
  CheckInLatitude: number;
  CheckInLongitude: number;
  CheckOutLatitude: number;
  CheckOutLongitude: number;
  TotalTime: string; // "-" or actual time string
  Report: string; // "-" or actual report text
  CheckInTime: string; // empty or datetime string
  CheckOutTime: string; // empty or datetime string
  IsSuperAdmin: boolean;
  IsOnLeave: boolean;
}

// Designation types and interfaces.
export interface Employee {
  Id: number;
  CompanyId: number;
  DesignationCode: string;
  Name: string;
  ParentId: number;
  ParentName: string;
  Description: string;
  IsActive: boolean;
  CreatedDate: string; // consider Date if you parse it
  ModifiedDate: string; // consider Date if you parse it
  CreatedByName: string;
  ModifiedByName: string;
}

export interface DesignationPayload {
  Description: string;
  DesignationCode: string;
  IsActive: string; // API returns "true" as string
  Name: string;
  ParentId: number;
}

export interface Branch {
  Id: number;
  CompanyId: number;
  BranchCode: string;
  Name: string;
  ParentId: number;
  ParentName: string;
  BranchHeadId: number;
  BranchHead: string;
  Description: string;
  IsActive: boolean;
  CreatedDate: string; // consider Date if you parse it
  ModifiedDate: string; // consider Date if you parse it
  CreatedByName: string;
  ModifiedByName: string;
}

export interface CreateBranchPayload {
  BranchCode: string;
  BranchHeadId: number;
  Description: string;
  IsActive: boolean;
  Name: string;
  ParentId: number;
}

const ENDPOINTS = {
  DEPARTMENT: "api/Department",
  DEPARTMENT_HEADS: "/api/CRM/MyTeamWithGCDetail",
  EDIT_DEPARTMENT: (id: number) => `api/Department/${id}`,
  //Designation endpoints
  DESIGNATION: "/api/Designation",
  EDIT_DESIGNATION: (id: number) => `/api/Designation/${id}`,
  //Branch endpoints
  BRANCH: "/api/Branch",
  EDIT_BRANCH: (id: number) => `/api/Branch/${id}`,
};

const getDepartment = async (
  params?: any,
): Promise<ApiResponse<DepartmentResponse<Department>>> => {
  const response = await privateRequest.get<
    ApiResponse<DepartmentResponse<Department>>
  >(ENDPOINTS.DEPARTMENT + "s", {
    params,
  });

  return response?.data;
};

const getDepartmentHeads = async (
  payload?: any,
): Promise<ApiResponse<DepartmentHeadResponse>> => {
  const response = await privateRequest.post<
    ApiResponse<DepartmentHeadResponse>
  >(ENDPOINTS.DEPARTMENT_HEADS, payload);
  return response?.data;
};

const createDepartment = async (
  payload: DepartmentPayload,
): Promise<ApiResponse<"">> => {
  const response = await privateRequest.post<ApiResponse<"">>(
    ENDPOINTS.DEPARTMENT,
    payload,
  );
  return response?.data;
};

const updateDepartment = async (
  id: number,
  payload: DepartmentPayload,
): Promise<ApiResponse<true>> => {
  const response = await privateRequest.put<ApiResponse<true>>(
    ENDPOINTS.EDIT_DEPARTMENT(id),
    payload,
  );
  return response?.data;
};

const deleteDepartement = async (id: number): Promise<ApiResponse<true>> => {
  const response = await privateRequest.delete<ApiResponse<true>>(
    ENDPOINTS.EDIT_DEPARTMENT(id),
  );
  return response?.data;
};

//Designation services
const getDesignations = async (
  params?: any,
): Promise<ApiResponse<DepartmentResponse<Employee>>> => {
  const response = await privateRequest.get<
    ApiResponse<DepartmentResponse<Employee>>
  >(ENDPOINTS.DESIGNATION + "s", {
    params,
  });

  return response?.data;
};

const createDesignation = async (
  payload: DesignationPayload,
): Promise<ApiResponse<"">> => {
  const response = await privateRequest.post<ApiResponse<"">>(
    ENDPOINTS.DESIGNATION,
    payload,
  );
  return response?.data;
};

const updateDesignation = async (
  id: number,
  payload: DesignationPayload,
): Promise<ApiResponse<true>> => {
  const response = await privateRequest.put<ApiResponse<true>>(
    ENDPOINTS.EDIT_DESIGNATION(id),
    payload,
  );
  return response?.data;
};

const deleteDesignation = async (id: number): Promise<ApiResponse<true>> => {
  const response = await privateRequest.delete<ApiResponse<true>>(
    ENDPOINTS.EDIT_DESIGNATION(id),
  );
  return response?.data;
};

const getBranch = async (
  params?: any,
): Promise<ApiResponse<DepartmentResponse<Branch>>> => {
  const response = await privateRequest.get<
    ApiResponse<DepartmentResponse<Branch>>
  >(ENDPOINTS.BRANCH, {
    params,
  });

  return response?.data;
};

const createBranch = async (
  payload: CreateBranchPayload,
): Promise<ApiResponse<"">> => {
  const response = await privateRequest.post<ApiResponse<"">>(
    ENDPOINTS.BRANCH,
    payload,
  );
  return response?.data;
};

const updateBranch = async (
  id: number,
  payload: CreateBranchPayload,
): Promise<ApiResponse<true>> => {
  const response = await privateRequest.put<ApiResponse<true>>(
    ENDPOINTS.EDIT_BRANCH(id),
    payload,
  );
  return response?.data;
};

const deleteBranch = async (id: number): Promise<ApiResponse<true>> => {
  const response = await privateRequest.delete<ApiResponse<true>>(
    ENDPOINTS.EDIT_BRANCH(id),
  );
  return response?.data;
};

const OrganizationalUnitService = {
  getDepartment,
  createDepartment,
  getDepartmentHeads,
  updateDepartment,
  deleteDepartement,
  getDesignations,
  createDesignation,
  updateDesignation,
  deleteDesignation,
  getBranch,
  createBranch,
  updateBranch,
  deleteBranch,
};

export default OrganizationalUnitService;
